import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-white border rounded shadow text-sm text-gray-700">
        <p className="font-semibold text-indigo-700">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.dataKey === "completed" && "✅ Completed"}
            {entry.dataKey === "pending" && "⏳ Pending"}
            {entry.dataKey === "rejected" && "❌ Rejected"}: <strong>{entry.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DailyProductionGraphModal = ({ data, onClose, orderTotals = {}, rejectionData = [] }) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Group rejection data
  const groupedRejectionByOrderAndLine = {};
  (rejectionData || []).forEach((item) => {
    const date = item.date;
    if (date !== selectedDate) return;

    const order = item.cut_order_no || `Order-${item.order_id}`;
    const color = item.suborder_colour_name || `Color-${item.sub_order_id}`;
    const line = item.line?.trim();
    if (!line || line.toLowerCase() === "unknown") return;

    const key = `${order}__${line}`;
    if (!groupedRejectionByOrderAndLine[key]) groupedRejectionByOrderAndLine[key] = {};
    if (!groupedRejectionByOrderAndLine[key][color]) groupedRejectionByOrderAndLine[key][color] = 0;
    groupedRejectionByOrderAndLine[key][color] += Number(item.total_piece || 0);
  });

  // Group completed production
  const groupedByOrderAndLine = {};
  const totalsByOrder = {};

  (data || []).forEach((item) => {
    const date = item.date;
    if (date !== selectedDate) return;

    const order = item.cut_order_no || `Order-${item.order_id}`;
    const color = item.suborder_colour_name || `Color-${item.sub_order_id}`;
    const line = item.line?.trim();
    if (!line || line.toLowerCase() === "unknown") return;

    const key = `${order}__${line}`;
    if (!groupedByOrderAndLine[key]) groupedByOrderAndLine[key] = {};
    if (!groupedByOrderAndLine[key][color]) groupedByOrderAndLine[key][color] = 0;
    groupedByOrderAndLine[key][color] += item.total_piece;

    if (!totalsByOrder[order]) totalsByOrder[order] = { completed: 0, planned: 0 };
    totalsByOrder[order].completed += item.total_piece;
  });

  // Render charts for each order
  const charts = Object.entries(orderTotals).map(([order, colorMap]) => {
    const linesData = (data || [])
      .filter(
        (d) =>
          d.cut_order_no === order &&
          d.date === selectedDate &&
          d.line?.trim() &&
          d.line.toLowerCase() !== "unknown"
      )
      .reduce((acc, d) => {
        const line = d.line.trim();
        if (!acc[line]) acc[line] = {};
        acc[line][d.suborder_colour_name] =
          (acc[line][d.suborder_colour_name] || 0) + d.total_piece;
        return acc;
      }, {});

    if (Object.keys(linesData).length === 0) {
      linesData["No Line Info"] = {};
    }

    const totalPlanned = Object.values(colorMap).reduce((sum, val) => sum + val, 0);
    const totalCompleted = totalsByOrder[order]?.completed || 0;
    const totalPending = Math.max(totalPlanned - totalCompleted, 0);

    return (
      <div key={order} className="bg-white border rounded-lg shadow p-4 w-full">
        <h2 className="text-lg font-semibold text-indigo-800 mb-1">Order No: {order}</h2>
        <p className="text-sm text-gray-700 mb-4">
          📦 <strong>Order Quantity:</strong> {totalPlanned} &nbsp; | &nbsp;
          ✅ <strong>Completed:</strong> {totalCompleted} &nbsp; | &nbsp;
          ⏳ <strong>Pending:</strong> {totalPending}
        </p>

        <div className="flex flex-col gap-6">
          {Object.entries(linesData).map(([line, lineData]) => {
            const key = `${order}__${line}`;

            const chartData = Object.entries(colorMap).map(([color, planned]) => {
              const completed = groupedByOrderAndLine?.[key]?.[color] || 0;
              const pending = Math.max(planned - completed, 0);
              const rejected = groupedRejectionByOrderAndLine?.[key]?.[color] || 0;

              return {
                label: color,
                completed,
                pending,
                rejected,
                planned,
              };
            });

            const maxY = Math.max(...chartData.map((d) => d.planned + (d.rejected || 0)), 100);

            return (
              <div key={line} className="bg-gray-50 border rounded-md p-4 w-full">
                <h3 className="text-md font-semibold text-indigo-700 mb-1">
                  Line: <span className="text-sm text-gray-600">{line}</span>
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  📅 Date: <strong>{selectedDate}</strong>
                </p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={40} />
                    <YAxis domain={[0, maxY]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      layout="vertical"
                      verticalAlign="bottom"
                      align="left"
                      iconType="circle"
                      wrapperStyle={{
                        paddingTop: 10,
                        paddingLeft: 0,
                        lineHeight: "22px",
                      }}
                    />
                    <Bar dataKey="completed" stackId="a" fill="#4caf50" name="✅ Completed" barSize={40}>
                      <LabelList dataKey="planned" position="top" fontSize={12} />
                    </Bar>
                    <Bar dataKey="pending" stackId="a" fill="#ff6f61" name="⏳ Pending" barSize={40}>
                      <LabelList
                        dataKey="pending"
                        position="top"
                        fontSize={12}
                        formatter={(val) => (val > 0 ? val : "")}
                      />
                    </Bar>
                    <Bar dataKey="rejected" stackId="a" fill="#2196f3" name="❌ Rejected" barSize={40}>
                      <LabelList
                        dataKey="rejected"
                        position="top"
                        fontSize={12}
                        formatter={(val) => (val > 0 ? val : "")}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </div>
    );
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white w-full h-full p-6 overflow-y-auto">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500"
          onClick={onClose}
        >
          ✖
        </button>
        <h2 className="text-2xl font-bold text-indigo-800 mb-4">📊 Production Charts</h2>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mr-2">Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border px-3 py-1 rounded-md shadow-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
          {charts.length > 0 ? charts : (
            <p className="text-gray-500">No Production data for selected date.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyProductionGraphModal;
